import "./creationwizard.scss";
import * as React from "react";
import {
    DateTimeInput,
    Select,
    type SelectGroupType,
    type SelectOptionType,
    type SelectOptionValueType,
    Toggle,
} from "@onshopfront/components";
import moment, { type Moment } from "moment";
import { type BaseProductRepository, type LocalDatabaseProduct } from "#/Data/_Bases/BaseProductRepository.ts";
import { type DataSourceTables } from "#/Data/_Bases/DataSourceTables.ts";
import { determineDataSource } from "#/Data/DataStores/DataSource.ts";
import CurrencyInput from "../Forms/CurrencyInput";
import PercentageInput from "../Forms/PercentageInput";
import CustomWizardElement, {
    type CustomWizardElementEvent,
    type CustomWizardElementType,
} from "./CustomWizardElement";

interface CreationWizardOption extends SelectOptionType<SelectOptionValueType, { type?: string; inactive?: true; }> {}
interface CreationWizardGroup extends SelectGroupType<
    CreationWizardOption["value"],
    CreationWizardOption["additional"]
> {}

export interface WizardElement {
    type: string;
    field: string;
    multiple?: boolean;
    creatable?: boolean;
    placeholder?: string;
    default?: string;
    required?: boolean;
    time?: boolean;
    end?: boolean;
    step?: number;
    min?: number;
    max?: number;
    printName?: string;
    isClearable?: boolean; // Defaults to true
    __label?: string;
    dataSource?: string | Array<string>;
    minimumSearchSize?: number;
    options?: Array<CreationWizardOption>;
    customElement?: CustomWizardElementType;
}

interface Props {
    exiting: boolean;
    loaded: boolean;
    backwards: boolean;
    question: string;
    container: React.RefObject<HTMLDivElement | null>;
    elements: Array<WizardElement>;
    onCreate: (field: string, value: any, callback?: () => void) => void;
    onChange: (field: string, value: any, label?: string, callback?: () => void) => void;
    onNext: () => void;
    creationId: number;
}

interface State {
    loading: boolean;
    inDialog: boolean;
}

export default class CreationWizardPage extends React.Component<Props, State> {
    protected typeCache: Record<string, string> = {};

    protected pageRef: React.RefObject<HTMLDivElement | null>;

    constructor(props: Props) {
        super(props);

        this.state = {
            loading : false,
            inDialog: false,
        };

        this.pageRef = React.createRef();

        this.handleChange = this.handleChange.bind(this);
        this.handleSelectChange = this.handleSelectChange.bind(this);
        this.handleSelectCreate = this.handleSelectCreate.bind(this);
        this.handleToggleChange = this.handleToggleChange.bind(this);
        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.handleSelectKeyDown = this.handleSelectKeyDown.bind(this);
        this.handleCustomEvent = this.handleCustomEvent.bind(this);
    }

    public componentDidMount(): void {
        if(this.pageRef.current) {
            this.setState({
                inDialog: !!this.pageRef.current.closest("#dialog"),
            });
        }
    }

    protected getDataSourceName(dataSource: string): string {
        if(!this.typeCache[dataSource]) {
            const words = dataSource.split(/(?=[A-Z])/).map(word => {
                return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
            });

            // Singularize the last word
            const lastIndex = words.length - 1;

            if(lastIndex >= 0) {
                let lastWord = words[lastIndex];

                if(lastWord.endsWith("ies")) {
                    lastWord = lastWord.slice(0, -3) + "y";
                } else if(lastWord.endsWith("s")) {
                    lastWord = lastWord.slice(0, -1);
                }

                words[lastIndex] = lastWord;
            }

            this.typeCache[dataSource] = words.join(" ");
        }

        return this.typeCache[dataSource];
    }

    protected handleChange(e: React.FormEvent<HTMLInputElement>): void {
        this.props.onChange(e.currentTarget.dataset.field as string, e.currentTarget.value);
    }

    protected handleToggleChange(e: React.FormEvent<HTMLInputElement>): void {
        this.props.onChange(e.currentTarget.dataset.field as string, e.currentTarget.checked);
    }

    protected handleCalendarChange(field: string, value: Moment | null, end?: boolean): void {
        if(!value) {
            this.props.onChange(field, null);

            return;
        }

        if(end && value.hour() === 0 && value.minute() === 0) {
            value.hour(23);
            value.minute(59);
            value.second(59);
        }

        this.props.onChange(field, value.toISOString(true));
    }

    protected handleSelectChange(
        field: string,
        value: null | CreationWizardOption | Array<CreationWizardOption>,
        multiple: null | boolean = false
    ): void {
        if(value === null || multiple) {
            this.props.onChange(field, value);

            return;
        }

        this.props.onChange(
            field,
            (value as CreationWizardOption).value,
            (value as CreationWizardOption).label as string
        );
    }

    protected handleSelectCreate(
        field: string,
        value: string
    ): Promise<CreationWizardOption> {
        return new Promise((res, rej) => {
            if(typeof this.props.onCreate !== "function") {
                return rej("Field is not creatable");
            }

            this.props.onCreate(field, value);

            return res({ label: value, value: value, additional: {} });
        });
    }

    protected handleCustomEvent(index: number, e: CustomWizardElementEvent): void {
        const element = this.props.elements[index];

        if(!element.customElement || !element.customElement.onEvent) {
            return;
        }

        const result = element.customElement.onEvent(e);

        this.props.onChange(element.field, result.data, undefined, () => {
            if(result.next) {
                this.props.onNext();
            }
        });
    }

    protected handleKeyDown(e: React.KeyboardEvent<HTMLElement>): void {
        if(e.key === "Enter") {
            this.props.onNext();
        }
    }

    protected handleSelectKeyDown(element: WizardElement): void {
        if(element.default !== null && element.default !== "" && element.default?.length !== 0) {
            this.props.onNext();
        }
    }

    protected renderText(element: WizardElement, index: number): React.ReactNode {
        return (
            <input
                key={index}
                type="text"
                placeholder={element.placeholder}
                onChange={this.handleChange}
                data-field={element.field}
                value={element.default}
                autoFocus={index === 0}
                onKeyDown={this.handleKeyDown}
                data-testid="wizard-element-text"
            />
        );
    }

    protected renderNumber(element: WizardElement, index: number): React.ReactNode {
        return (
            <input
                key={index}
                type="number"
                step="any"
                placeholder={element.placeholder}
                onChange={this.handleChange}
                data-field={element.field}
                value={element.default}
                autoFocus={index === 0}
                onKeyDown={this.handleKeyDown}
                data-testid="wizard-element-number"
            />
        );
    }

    protected renderCurrency(element: WizardElement, index: number): React.ReactNode {
        return (
            <CurrencyInput
                key={index}
                placeholder={element.placeholder}
                onChange={this.handleChange}
                data-field={element.field}
                value={element.default}
                autoFocus={index === 0}
                onKeyDown={this.handleKeyDown}
                data-testid="wizard-element-currency"
            />
        );
    }

    protected renderPercentage(element: WizardElement, index: number): React.ReactNode {
        return (
            <PercentageInput
                key={index}
                placeholder={element.placeholder}
                onChange={this.handleChange}
                data-field={element.field}
                value={element.default}
                autoFocus={index === 0}
                onKeyDown={this.handleKeyDown}
                data-testid="wizard-element-percentage"
            />
        );
    }

    protected searchSelectDatasource(
        search: string,
        dataSource: keyof DataSourceTables
    ): Promise<Array<CreationWizardOption>> {
        const needle = search.toLowerCase();

        return determineDataSource()
            .then(async source => {
                let data: Array<any>;

                let searched = false;

                if("search" in source[dataSource] && typeof source[dataSource].search === "function") {
                    searched = true;

                    if(dataSource === "products") {
                        data = await (source[dataSource] as BaseProductRepository).search(search, {
                            name   : true,
                            barcode: true,
                            exact  : false,
                        });
                    } else {
                        data = await source[dataSource].search(search);
                    }
                } else {
                    data = await source[dataSource].all();
                }

                const type = this.getDataSourceName(dataSource);

                // Convert the data
                const options = data.map(item => {
                    const option: CreationWizardOption = {
                        label     : item.name,
                        value     : item.uuid,
                        additional: {
                            type,
                        },
                    };

                    if(dataSource === "products") {
                        if((item as LocalDatabaseProduct).status === "inactive") {
                            option.additional = {
                                ...option.additional,
                                inactive: true,
                            };
                        }
                    }

                    return option;
                });

                // Sort the values
                options.sort((a, b) => {
                    return (a.label as string).localeCompare(b.label as string);
                });

                if(!needle || searched) {
                    return options;
                }

                return options.filter(option => {
                    return (option.label as string).toLowerCase().includes(needle);
                });
            });
    }

    protected prepareLabelRender(
        element: WizardElement
    ): undefined | ((option: CreationWizardOption) => React.ReactNode) {
        const options: Array<CreationWizardOption> | undefined = element.options;

        if(Array.isArray(options)) {
            return undefined;
        }

        if(
            typeof element.dataSource !== "string" &&
            !(Array.isArray(element.dataSource) && element.dataSource.length)
        ) {
            return undefined;
        }

        // eslint-disable-next-line react/display-name
        return option => {
            let status: React.ReactNode = null;

            if(option.additional?.inactive) {
                status = (
                    <span className="text-neutral-600 dark:text-neutral-400">
                        - Inactive
                    </span>
                );
            }

            return (
                <>{option.label} {status}</>
            );
        };
    }

    protected renderSelect(element: WizardElement, index: number): React.ReactNode {
        let value = null;

        if(element.default) {
            if(Array.isArray(element.default)) {
                value = element.default as Array<CreationWizardOption>;
            } else {
                value = {
                    value: element.default,
                    label: element.__label,
                } as CreationWizardOption;
            }
        }

        return (
            <Select<boolean, boolean, CreationWizardOption["value"], CreationWizardOption["additional"]>
                key={`${index}.${element.field}`}
                isMulti={element.multiple}
                clearOnSelect={element.multiple}
                onChange={value => {
                    this.handleSelectChange(element.field, value, element.multiple);
                }}
                data-field={element.field}
                data-testid="wizard-element-select"
                value={value}
                autoFocus={index === 0}
                isClearable={typeof element.isClearable === "boolean" ? element.isClearable : true}
                className="creation-wizard-select w-full"
                canCreate={element.creatable}
                onCreate={value => this.handleSelectCreate(element.field, value)}
                renderLabel={this.prepareLabelRender(element)}
                minimumSearchSize={(
                    typeof element.minimumSearchSize === "number" ? (
                        element.minimumSearchSize
                    ) : (
                        (Array.isArray(element.dataSource) && element.dataSource.length !== 0) ?
                            3 :
                            undefined
                    )
                )}
                options={async search => {
                    let options: Array<CreationWizardOption | CreationWizardGroup> | undefined = element.options;

                    if(Array.isArray(options)) {
                        if(!search) {
                            return options;
                        }

                        search = search.toLowerCase();

                        return options.filter(option => {
                            return (option.label as string).toLowerCase().includes(search);
                        });
                    } else if(typeof element.dataSource === "string") {
                        return this.searchSelectDatasource(
                            search,
                            element.dataSource as keyof DataSourceTables
                        );
                    } else if(Array.isArray(element.dataSource) && element.dataSource.length) {
                        if(search.length < 2) {
                            return [];
                        }

                        // Sort the data sources
                        element.dataSource.sort((a, b) => {
                            return a.localeCompare(b);
                        });

                        // Get all the data sources and prepare the lists
                        options = [];

                        for(let i = 0, l = element.dataSource.length; i < l; i++) {
                            const type = this.getDataSourceName(element.dataSource[i]);

                            const data = await this.searchSelectDatasource(
                                search,
                                element.dataSource[i] as keyof DataSourceTables
                            )
                                .catch(() => {
                                    return [] as Array<CreationWizardOption>;
                                });

                            if(data.length) {
                                options.push({
                                    label  : type,
                                    options: data,
                                });
                            }
                        }

                        return options;
                    }

                    return [];
                }}
            />
        );
    }

    protected renderToggle(element: WizardElement, index: number): React.ReactNode {
        return (
            <label
                key={index}
                className="creation-wizard-toggle"
            >
                <Toggle
                    data-testid="wizard-element-toggle"
                    data-field={element.field}
                    name={element.field}
                    checked={!!element.default}
                    onChange={this.handleToggleChange}
                />
                {element.placeholder}
            </label>
        );
    }

    protected renderCalender(element: WizardElement, index: number): React.ReactNode {
        return (
            <DateTimeInput
                key={index}
                withTime={element.time}
                className="w-full mb-2"
                value={[ !element.default ? null : moment(element.default).toDate() ]}
                isClearable={element.isClearable}
                onChange={([ start ]): void => {
                    this.handleCalendarChange(
                        element.field,
                        start ? moment(start) : null,
                        element.end
                    );
                }}
                placeholder={element.placeholder}
                closeOnSelect
            />
        );
    }

    protected renderCustom(element: WizardElement, index: number): React.ReactNode {
        return (
            <CustomWizardElement
                element={element.customElement as CustomWizardElementType}
                onEvent={this.handleCustomEvent}
                elementIndex={index}
                key={index}
                creationId={this.props.creationId}
            />
        );
    }

    protected renderElements(): Array<React.ReactNode> {
        const elements = [];

        for(let i = 0, l = this.props.elements.length; i < l; i++) {
            switch(this.props.elements[i].type) {
                case "text":
                    elements.push(this.renderText(this.props.elements[i], i));
                    break;
                case "number":
                    elements.push(this.renderNumber(this.props.elements[i], i));
                    break;
                case "currency":
                    elements.push(this.renderCurrency(this.props.elements[i], i));
                    break;
                case "percentage":
                    elements.push(this.renderPercentage(this.props.elements[i], i));
                    break;
                case "select":
                    elements.push(this.renderSelect(this.props.elements[i], i));
                    break;
                case "toggle":
                    elements.push(this.renderToggle(this.props.elements[i], i));
                    break;
                case "calendar":
                    elements.push(this.renderCalender(this.props.elements[i], i));
                    break;
                case "custom":
                    elements.push(this.renderCustom(this.props.elements[i], i));
                    break;
                default:
                    console.error("Unidentified element", this.props.elements[i]);
                    throw new Error("Unidentified element from script.");
            }
        }

        return elements;
    }

    public render(): React.ReactNode {
        if(!this.props.loaded) {
            return (
                <div ref={this.pageRef} className="creation-wizard-page" />
            );
        }

        const classes = [ "creation-wizard-page" ];

        if(this.props.loaded) {
            classes.push("creation-wizard-page-loaded");
        }

        if(this.props.exiting) {
            classes.push("creation-wizard-page-exiting");
        }

        if(this.props.backwards) {
            classes.push("creation-wizard-page-backwards");
        }

        return (
            <div ref={this.pageRef} className={classes.join(" ")}>
                <div className="creation-wizard-page-header">
                    <p>{this.props.question}</p>
                </div>
                <div className="creation-wizard-page-body">
                    {this.renderElements()}
                </div>
            </div>
        );
    }
}
